import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadCloud, FileText, ShieldCheck, Check, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import docshawkService from '../../services/docshawkService';
import pipelineService, { PipelineRun } from '../../services/pipelineService';
import { useAgentKey } from '../../hooks/useAgentKey';
import { Spinner, Button } from '../../components/ui';
import '../../styles/theme.css';
import '../../styles/review-theme.css';
import '../../styles/review-workspace.css';

// Turn a run's raw state into an outcome sentence a reviewer understands.
function outcome(run: PipelineRun): { label: string; dot: string; cta?: string; icon?: React.ReactNode } {
  const gate = run.hitl_gate?.gate;
  if (gate === 'sanity_check') return { label: 'Needs your review', dot: 'var(--dh-review)', cta: 'Review →' };
  if (gate === 'schema_approval') return { label: 'Confirm the terms to track', dot: 'var(--dh-review)', cta: 'Set up →' };
  if (run.status === 'succeeded') return { label: 'Complete — reviewed by you', dot: 'var(--dh-verified)',
    icon: <Check size={14} style={{ color: 'var(--dh-verified)' }} />, cta: 'Results' };
  if (run.status === 'running' || run.status === 'cancelling')
    return { label: 'Reading your document…', dot: 'var(--dh-accent)', cta: 'Watch →' };
  if (run.status === 'failed') {
    const rejected = run.stages?.some((s) => s.stage === 'extract' && s.status === 'skipped');
    return rejected
      ? { label: 'Doesn’t appear to be a loan agreement — not analyzed', dot: 'var(--dh-ink-faint)' }
      : { label: 'Couldn’t process this document', dot: 'var(--dh-danger)', cta: 'Open' };
  }
  return { label: run.status, dot: 'var(--dh-ink-faint)', cta: 'Open' };
}

const DocShawkDocuments: React.FC = () => {
  const navigate = useNavigate();
  const agentKey = useAgentKey();   // "doc" for the existing view; carried through every call
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [confidential, setConfidential] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchRuns = useCallback(async () => {
    try { setRuns((await pipelineService.listRuns(1, 50, undefined, agentKey)).runs); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, [agentKey]);

  useEffect(() => {
    fetchRuns();
    const t = setInterval(fetchRuns, 4000);
    return () => clearInterval(t);
  }, [fetchRuns]);

  const upload = async (f: File) => {
    setUploading(true); setMessage('');
    try {
      const res = await docshawkService.uploadDocument(f, confidential ? 'confidential' : 'public', undefined, agentKey);
      setFile(null);
      for (let i = 0; i < 20; i++) {
        const data = await pipelineService.listRuns(1, 20, undefined, agentKey);
        const run = data.runs.find((r) => r.document_id === res.document.id);
        if (run) { navigate(`/admin/docshawk/runs/${run.id}`); return; }
        await new Promise((r) => setTimeout(r, 300));
      }
      setMessage(res.created ? `Uploaded ${res.document.filename}.` : `Already analyzed: ${res.document.filename}`);
      fetchRuns();
    } catch (e: any) {
      setMessage(e?.response?.data?.detail || 'Upload failed. Try a text-based PDF or a .txt file.');
    } finally { setUploading(false); }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); upload(f); }
  };
  const onPick = (f: File | null) => { if (f) { setFile(f); upload(f); } };

  return (
    <div className="dh-review">
      <h1 className="dh-h1">Review a loan or credit agreement</h1>
      <p className="dh-sub">DocShawk reads your agreement, pulls out the key terms, and shows you exactly where each came from.</p>

      <div className={`dh-drop ${dragging ? 'drag' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)} onDrop={onDrop}
        onClick={() => inputRef.current?.click()}>
        {uploading ? <Loader2 size={30} className="mx-spin" style={{ color: 'var(--dh-accent)' }} />
          : <UploadCloud size={32} style={{ color: 'var(--dh-accent)' }} />}
        <div className="dh-drop-t">{uploading ? 'Opening your document…' : file ? file.name : 'Drop your agreement here, or browse files'}</div>
        <div className="dh-drop-s">PDF or text, up to 500 pages</div>
        <input ref={inputRef} type="file" accept=".pdf,.txt" hidden
          onChange={(e) => onPick(e.target.files?.[0] || null)} />
        <div className="dh-promise" onClick={(e) => e.stopPropagation()}>
          <ShieldCheck size={20} />
          <div className="t"><b>Confidential by default.</b> Analyzed only within your firm’s approved systems —
            never sent to public AI services.
            <label className="dh-chk">
              <input type="checkbox" checked={confidential} onChange={(e) => setConfidential(e.target.checked)} />
              This document is confidential
            </label>
          </div>
        </div>
      </div>
      {message && <div className="dh-msg">{message}</div>}

      <div className="dh-listhead">
        <span className="h">Your documents</span>
        <Button icon={RefreshCw} variant="ghost" size="sm" onClick={fetchRuns}>Refresh</Button>
      </div>

      {loading && !runs.length ? (
        <div className="dh-docs"><div style={{ padding: 20 }}><Spinner label="Loading…" /></div></div>
      ) : !runs.length ? (
        <div className="dh-docs"><div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--dh-ink-faint)' }}>
          <FileText size={26} /><div style={{ marginTop: 8, fontSize: 13.5 }}>
            No documents yet — upload an agreement and DocShawk will pull out the key terms, each with a citation.</div></div></div>
      ) : (
        <div className="dh-docs">
          {runs.map((run) => {
            const o = outcome(run);
            return (
              <div key={run.id} className="dh-doc" onClick={() => navigate(`/admin/docshawk/runs/${run.id}`)}>
                <FileText className="fi" size={26} strokeWidth={1.4} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="fn">{run.document_filename}</div>
                  <div className="st">{o.icon || <span className="d" style={{ background: o.dot }} />}{o.label}</div>
                </div>
                <span className="dh-when">{run.created_at ? new Date(run.created_at).toLocaleString() : ''}</span>
                {o.cta && <button className="dh-cta" onClick={(e) => { e.stopPropagation();
                  navigate(o.cta === 'Results' ? `/admin/docshawk/runs/${run.id}/results` : `/admin/docshawk/runs/${run.id}`); }}>
                  {o.cta}</button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DocShawkDocuments;
